import type { Messages } from "../i18n";
import { ApiError } from "./api";

/**
 * Turns a thrown creation error into something a person can act on, in
 * their own language.
 *
 * Before i18n this surfaced `ApiError.message` directly — "API error 400:
 * webhook_host_not_permitted" — which was neither translatable nor
 * meaningful to a reader. The codes below are the ones `api/src/routes`
 * actually returns on the create path; anything else, including a network
 * failure with no response at all, gets the generic line rather than a
 * server-authored string of unknown language and unknown content.
 */
export function createErrorMessage(err: unknown, m: Messages): string {
  if (!(err instanceof ApiError)) return m.create.error.generic;

  switch (err.body?.error) {
    case "webhook_host_not_permitted":
      return m.apiError.webhookHostNotPermitted;
    case "webhooks_disabled":
      return m.apiError.webhooksDisabled;
    case "validation_failed":
    case "invalid_meta_header":
    case "missing_meta_header":
      return m.apiError.validationFailed;
    case "unauthenticated":
      return m.apiError.unauthenticated;
  }

  // Status-only cases: @fastify/rate-limit and Fastify's own body-limit
  // reply with their own generic bodies, not one of our codes.
  switch (err.status) {
    case 401:
      return m.apiError.unauthenticated;
    case 413:
      return m.apiError.tooLarge;
    case 429:
      return m.apiError.rateLimited;
  }

  return m.create.error.generic;
}
